import {
  CATCH_BOARD_STAGE,
  getCatchBoardStageMeta,
} from 'src/screens/catch-board/catchBoardConstants'
import { fireCrowsNestConfetti } from 'src/utils/crowsNestConfetti'

/** Undo toast stays visible long enough to click. */
export const CATCH_BOARD_UNDO_TOAST_DURATION_S = 5

/** Landed celebration — bigger and longer than Crow’s Nest “new lead” sparkle. */
export const CATCH_BOARD_LANDED_CONFETTI = Object.freeze({
  particleCount: 160,
  durationMs: 3200,
  spread: 2.2,
  speed: 1.55,
  sizeScale: 1.45,
})

/**
 * Short column label for toasts (e.g. "Hooked", not "Hooked / Interested").
 * @param {string} stage
 */
export function getCatchBoardShortStageLabel(stage) {
  return getCatchBoardStageMeta(stage)?.label || stage || ''
}

/**
 * Copy for the post-drag undo toast (before the "Undo" action).
 * @param {{ fromStage: string, toStage: string }} params
 */
export function buildCatchBoardMoveUndoMessage({ fromStage, toStage }) {
  const toLabel = getCatchBoardShortStageLabel(toStage)
  if (!toLabel) {
    return 'Deal moved'
  }
  if (fromStage === toStage) {
    return `Reordered in ${toLabel}`
  }
  return `Moved to ${toLabel}`
}

/**
 * Confetti only when a deal newly lands (not reorder within Landed).
 * @param {string} fromStage
 * @param {string} toStage
 */
export function shouldCelebrateCatchBoardLanded(fromStage, toStage) {
  return (
    toStage === CATCH_BOARD_STAGE.LANDED &&
    fromStage !== CATCH_BOARD_STAGE.LANDED
  )
}

/**
 * Over-the-top Landed celebration: centre blast + delayed side cannons.
 * @param {{ particleCount?: number, durationMs?: number, spread?: number, speed?: number, sizeScale?: number }} [options]
 * @returns {() => void} cancel
 */
export function fireCatchBoardLandedConfetti(options = {}) {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const settings = {
    ...CATCH_BOARD_LANDED_CONFETTI,
    ...options,
  }

  const width = window.innerWidth || 1200
  const height = window.innerHeight || 800
  const cancels = []
  const timers = []

  cancels.push(
    fireCrowsNestConfetti({
      ...settings,
      originX: width / 2,
      originY: Math.min(140, height * 0.2),
    }),
  )

  timers.push(
    window.setTimeout(() => {
      cancels.push(
        fireCrowsNestConfetti({
          particleCount: Math.round(settings.particleCount * 0.55),
          durationMs: Math.round(settings.durationMs * 0.85),
          spread: settings.spread * 0.95,
          speed: settings.speed,
          sizeScale: settings.sizeScale,
          originX: width * 0.18,
          originY: height * 0.35,
        }),
      )
    }, 160),
  )

  timers.push(
    window.setTimeout(() => {
      cancels.push(
        fireCrowsNestConfetti({
          particleCount: Math.round(settings.particleCount * 0.55),
          durationMs: Math.round(settings.durationMs * 0.85),
          spread: settings.spread * 0.95,
          speed: settings.speed,
          sizeScale: settings.sizeScale,
          originX: width * 0.82,
          originY: height * 0.35,
        }),
      )
    }, 280),
  )

  timers.push(
    window.setTimeout(() => {
      cancels.push(
        fireCrowsNestConfetti({
          particleCount: Math.round(settings.particleCount * 0.4),
          durationMs: Math.round(settings.durationMs * 0.7),
          spread: settings.spread * 1.1,
          speed: settings.speed * 1.1,
          sizeScale: settings.sizeScale * 1.1,
          originX: width / 2,
          originY: height * 0.55,
        }),
      )
    }, 420),
  )

  return () => {
    timers.forEach(id => window.clearTimeout(id))
    cancels.forEach(cancel => {
      try {
        cancel?.()
      } catch {
        // ignore
      }
    })
  }
}
